import { useState, useEffect } from 'react';
import ProjectModal from '../components/ProjectModal';
import SEO from '../components/SEO';
import { portfolioService, type PublicProject } from '../services/portfolioService';

const PortfolioPage = () => {
  const [projects, setProjects] = useState<PublicProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<PublicProject | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    portfolioService.getAll().then((data) => {
      setProjects(data);
      setLoading(false);
    });
  }, []);

  const openModal = (project: PublicProject) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedProject(null), 300);
  };

  const stats = [
    { number: '5+', label: 'Platforms Built' },
    { number: '100%', label: 'Client Satisfaction' },
    { number: '3', label: 'Cloud Providers' },
    { number: '10+', label: 'Years Experience' },
  ];

  return (
    <div>
      <SEO
        title="Our Portfolio - Successful Projects & Implementations"
        description="Explore Kommonview's successful projects including Akademus (EdTech platform), Ovasyt (background verification), Elelwani Catering Services (digital branding), FPT Academy (security hardening), and Sambe Consulting (BI analytics)."
        keywords="portfolio, projects, Akademus, Ovasyt, Elelwani Catering Services, FPT Academy, Sambe Consulting, case studies, client work, technology projects, South Africa"
        url="https://kommonview.com/portfolio"
      />

      {/* Hero Section */}
      <section className="bg-primary text-white py-20 lg:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6" data-aos="slide-right">
              Our Portfolio
            </h1>
            <p className="text-xl text-gray-100" data-aos="slide-left" data-aos-delay="100">
              Explore our successful projects and implementations
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          {loading ? (
            /* Skeleton loader — matches the card grid layout */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden shadow-lg animate-pulse">
                  <div className="h-72 bg-gray-200" />
                  <div className="p-6 space-y-3">
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                    <div className="h-5 bg-gray-200 rounded w-2/3" />
                    <div className="h-4 bg-gray-200 rounded w-full" />
                    <div className="h-4 bg-gray-200 rounded w-4/5" />
                  </div>
                </div>
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No projects available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
                >
                  <div className="bg-white h-72 flex items-center justify-center p-8 border-b border-gray-100">
                    {project.logo ? (
                      <img
                        src={project.logo}
                        alt={`${project.title} logo`}
                        className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300 rounded-2xl"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-primary/10 rounded-2xl flex items-center justify-center">
                        <i className="fas fa-briefcase text-4xl text-primary/40" />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="text-sm text-accent font-semibold mb-2 uppercase tracking-wide">
                      {project.category}
                    </div>
                    <h3 className="text-xl font-heading font-bold mb-3 text-gray-900 group-hover:text-primary transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4">{project.description}</p>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => openModal(project)}
                        className="flex-1 flex items-center justify-center text-primary font-medium hover:text-primary-700 transition-colors"
                      >
                        <span>Read More</span>
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                      {project.url && (
                        <a
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center text-primary font-medium hover:text-primary-700 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span>Visit Site</span>
                          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <ProjectModal
          isOpen={isModalOpen}
          onClose={closeModal}
          project={selectedProject}
        />
      )}
    </div>
  );
};

export default PortfolioPage;
